import React, { useMemo } from "react";
import { t, Trans, useI18n } from "@bookingcom/lingojs-react";
import useFetchAlternativeFares from "../../../hooks/useFetchAlternativeFares";
import useClientCachedURL from "../../../hooks/useClientCachedURL";
import { useHistory, useLocation } from "react-router-dom";
import useGetFlightsRankingUrl from "../../../hooks/useGetFlightsRankingUrl";
import { UIAlternativeFaresReason, UIFlightData } from "@flights/types";
import { flightsLocalStore, flightsSessionStore } from "@flights/web-api-utils-universal";
import {
  ALTERNATIVE_FARES_FLIGHT_OFFER_OBJECT_KEY,
  CURRENT_ORDER_ID_KEY,
  CURRENT_ORDER_TOKEN_KEY,
  SIRF_AVAILABLE_KEY
} from "../../../constants";
import { PAYMENT_CAPTURED_PARAM, REASON_PARAM } from "../../../component-controllers/CheckoutPaymentController";
import { useEffect } from "react";
import flights_alternative_fares_for_price_change_and_sold_out_errors_web from "../../../utils/experiments/order/flights_alternative_fares_for_price_change_and_sold_out_errors_web";
import { Link } from "@bookingcom/bui-react";

const useGetInfoFromUrl = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const paymentCaptured = searchParams.get(PAYMENT_CAPTURED_PARAM);
  const reason = searchParams.get(REASON_PARAM);

  return {
    hasPaymentBeenCaptured: paymentCaptured && paymentCaptured === "1" ? true : false,
    reason: reason ? (reason as UIAlternativeFaresReason) : undefined
  };
};

const useCheckoutAlternativeFaresController = () => {
  const i18n = useI18n();
  const history = useHistory();
  const { hasPaymentBeenCaptured, reason } = useGetInfoFromUrl();

  const flight = flightsLocalStore.get(ALTERNATIVE_FARES_FLIGHT_OFFER_OBJECT_KEY) as UIFlightData;
  const orderId: string = flightsLocalStore.get(CURRENT_ORDER_ID_KEY);

  const { getLastSuccessfulSearchUrl } = useClientCachedURL();
  const lastSearchUrl = getLastSuccessfulSearchUrl();
  const { alternativeFares, alternativeFaresLoading, alternativeFaresError } = useFetchAlternativeFares({
    soldOutOffer: {
      segments: flight?.segments,
      priceBreakdown: flight?.priceBreakdown,
      tripType: flight?.tripType
    },
    orderId,
    reason: reason || "FARE_SOLD_OUT"
  });
  const flightsRankingUrl = useGetFlightsRankingUrl();

  useEffect(() => {
    if (alternativeFaresError) {
      flights_alternative_fares_for_price_change_and_sold_out_errors_web.goals.alt_fares_failed_load();
    }
  }, [alternativeFaresError]);

  const bookingDetailsPageUrl = useMemo(() => {
    const orderToken = flightsLocalStore.get(CURRENT_ORDER_TOKEN_KEY);

    if (!orderToken) return undefined;

    const { origin, search } = new URL(window.location.href);
    const linkUrl = new URL(`/booking/order-details/${orderToken}/${search}`, origin);

    return linkUrl.href;
  }, []);

  return {
    alternativeFares,
    alternativeFaresLoading,
    alternativeFaresError,
    flightsRankingUrl,
    title: i18n.trans(t("flights_bp_failed_no_payment_collected_title")),
    subtitle:
      hasPaymentBeenCaptured && bookingDetailsPageUrl
        ? [
            <Trans
              key="0"
              tag="flights_pb_failed_alternative_subheader_1"
              variables={{
                start_bold: "<0>",
                end_bold: "</0>",
                start_link: "<1>",
                end_link: "</1>"
              }}
              components={[
                <strong key="0" />,
                <Link key="1" attributes={{ target: "__blank" }} href={bookingDetailsPageUrl} />
              ]}
            />,
            i18n.trans(t("flights_pb_failed_alternative_subheader_2"))
          ]
        : i18n.trans(t("flights_bp_failed_no_payment_collected_subtitle")),
    errorCta: i18n.trans(t("flights_bp_failed_view_all_cta")),
    onErrorClick: () => {
      history.push(lastSearchUrl);
    },
    isSirfAvailable: flightsSessionStore.get(SIRF_AVAILABLE_KEY)
  };
};

export default useCheckoutAlternativeFaresController;
